import React, { useState, useEffect } from 'react';
import { Row, Col, Progress, Space, Table, Tag, Card, Typography, Grid, Divider, Input, Pagination } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import {
  FlightDateText,
  PrimaryTableRowText,
  StandardTableRowText,
  TableColumnTitle,
} from '../../../shared/globalStyling/styledText';
import Button from '../../../shared/globalStyling/Button';
import moment from 'moment';
import { formatCurrency2SigFig, formatWholeNumber } from '../../../../core/utils/campaigns';

const Frag = React.Fragment;
const { useBreakpoint } = Grid;
const EditorialTable = ({
  data,
  history,
  selectedRowKeys,
  onRowChange,
  location,
  total,
  current,
  pageSize,
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;
  const useCardLayout = !screens.lg;
  const [localPageSize, setLocalPageSize] = useState(10);
  const [localCurrent, setLocalCurrent] = useState(1);

  useEffect(() => {
    const savedPageSize = sessionStorage.getItem('editorialMainPageSize');
    if (savedPageSize) {
      setLocalPageSize(Number(savedPageSize));
    }
  }, []);

  useEffect(() => {
    if (data && data.length > 0) {
      const savedPageSize = Number(sessionStorage.getItem('editorialMainPageSize')) || 10;
      const savedCurrentPage = Number(sessionStorage.getItem('editorialMainCurrentPage')) || 1;

      const maxPage = Math.ceil(data.length / savedPageSize);
      const validPage = savedCurrentPage <= maxPage && savedCurrentPage > 0 ? savedCurrentPage : 1;

      if (validPage !== localCurrent) {
        setLocalCurrent(validPage);
        // Update session storage with the validated page number
        if (validPage !== savedCurrentPage) {
          sessionStorage.setItem('editorialMainCurrentPage', validPage.toString());
        }
      }
    }
  }, [data, localCurrent]);

  const renderMobileCard = (record) => {
    const startDate = record.startDate ? moment(record.startDate) : null;
    const endDate = record.endDate ? moment(record.endDate) : null;
    const currentDate = moment();
    let flightProgress = 0;
    if (currentDate.isAfter(endDate)) flightProgress = 100;
    else if (currentDate.isBetween(startDate, endDate))
      flightProgress =
        (currentDate.diff(startDate, 'minutes') / endDate.diff(startDate, 'minutes')) * 100;

    const statusConfig = {
      ACTIVE: {
        backgroundColor: '#00DE5133',
        color: '#00DE51',
        text: 'Live',
      },
      DEMO: {
        backgroundColor: '#b9b6b633',
        color: '#b9b6b6',
        text: 'Demo',
      },
      PAUSED: {
        backgroundColor: 'orange',
        color: 'white',
        text: 'Paused',
      },
    };
    const status = record.status || 'ACTIVE';

    return (
      <Card
        style={{
          marginBottom: isMobile ? 16 : 20,
          borderRadius: 8,
          cursor: 'pointer',
        }}
        bodyStyle={{ padding: isMobile ? 20 : 24 }}
        onClick={() => history.push(`/publisher/details/${record.id}/1`)}
        hoverable
      >
        <Row gutter={[12, 8]}>
          {/* Header with Name and Status */}
          <Col span={24}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 4 }}>
              <Col flex="auto">
                <Typography.Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
                  {record.name || '-'}
                </Typography.Text>
              </Col>
              <Col>
                <Tag
                  style={{
                    ...statusConfig[status],
                    border: 0,
                    borderRadius: '25px',
                    padding: '2px 12px',
                  }}
                >
                  <Row justify="space-around" type="flex" align="middle" gutter={4}>
                    <Col>
                      <div
                        style={{
                          width: '5px',
                          height: '5px',
                          borderRadius: '50px',
                          backgroundColor: statusConfig[status].color,
                        }}
                      />
                    </Col>
                    <Col>{statusConfig[status].text}</Col>
                  </Row>
                </Tag>
              </Col>
            </Row>
            <Divider style={{ margin: '8px 0' }} />
          </Col>

          {/* Date Range */}
          <Col span={24} style={{ paddingBottom: 4 }}>
            <div>
              <Row align="middle" gutter={8}>
                <Col>
                  <CalendarOutlined style={{ fontSize: isMobile ? 13 : 14, color: '#8c8c8c' }} />
                </Col>
                <Col>
                  <Typography.Text style={{ fontSize: isMobile ? 12 : 13 }}>
                    {startDate ? startDate.format('MM/DD/YY') : 'N/A'} → {endDate ? endDate.format('MM/DD/YY') : 'N/A'}
                  </Typography.Text>
                </Col>
              </Row>
              <Progress
                type={'line'}
                strokeWidth={3}
                percent={flightProgress || 0}
                showInfo={false}
                strokeColor={'#636363'}
                style={{ marginTop: 8, maxWidth: isMobile ? '60%' : '50%' }}
              />
            </div>
          </Col>

          <Col span={24}>
            <Divider style={{ margin: '8px 0' }} />
          </Col>

          {/* Sales and Sessions in Two Columns */}
          <Col span={12} style={{ paddingRight: isMobile ? 16 : 24 }}>
            <Typography.Text strong style={{ fontSize: isMobile ? 11 : 12, display: 'block', marginBottom: 6 }}>
              Sales
            </Typography.Text>
            <Row style={{ marginTop: 4 }}>
              <Col span={14}>
                <Typography.Text type="secondary" style={{ fontSize: isMobile ? 10 : 11 }}>
                  Attr. Page Views:
                </Typography.Text>
              </Col>
              <Col span={10} style={{ textAlign: 'right' }}>
                <Typography.Text style={{ fontSize: isMobile ? 11 : 12 }}>
                  {formatWholeNumber(record.clicks)}
                </Typography.Text>
              </Col>
            </Row>
            <Row style={{ marginTop: 4 }}>
              <Col span={14}>
                <Typography.Text type="secondary" style={{ fontSize: isMobile ? 10 : 11 }}>
                  Customers:
                </Typography.Text>
              </Col>
              <Col span={10} style={{ textAlign: 'right' }}>
                <Typography.Text style={{ fontSize: isMobile ? 11 : 12 }}>
                  {formatWholeNumber(record.customers)}
                </Typography.Text>
              </Col>
            </Row>
            <Row style={{ marginTop: 4 }}>
              <Col span={14}>
                <Typography.Text type="secondary" style={{ fontSize: isMobile ? 10 : 11 }}>
                  Transactions:
                </Typography.Text>
              </Col>
              <Col span={10} style={{ textAlign: 'right' }}>
                <Typography.Text style={{ fontSize: isMobile ? 11 : 12 }}>
                  {formatWholeNumber(record.transactions)}
                </Typography.Text>
              </Col>
            </Row>
            <Row style={{ marginTop: 4 }}>
              <Col span={14}>
                <Typography.Text type="secondary" style={{ fontSize: isMobile ? 10 : 11 }}>
                  Revenue:
                </Typography.Text>
              </Col>
              <Col span={10} style={{ textAlign: 'right' }}>
                <Typography.Text style={{ fontSize: isMobile ? 11 : 12 }}>
                  {formatCurrency2SigFig(record.revenue)}
                </Typography.Text>
              </Col>
            </Row>
          </Col>

          <Col span={12} style={{ paddingRight: isMobile ? 16 : 24 }}>
            <Typography.Text strong style={{ fontSize: isMobile ? 11 : 12, display: 'block', marginBottom: 6 }}>
              Sessions
            </Typography.Text>
            <Row style={{ marginTop: 4 }}>
              <Col span={14}>
                <Typography.Text type="secondary" style={{ fontSize: isMobile ? 10 : 11 }}>
                  Sessions:
                </Typography.Text>
              </Col>
              <Col span={10} style={{ textAlign: 'right' }}>
                <Typography.Text style={{ fontSize: isMobile ? 11 : 12 }}>
                  {formatWholeNumber(record.sessions)}
                </Typography.Text>
              </Col>
            </Row>
            <Row style={{ marginTop: 4 }}>
              <Col span={14}>
                <Typography.Text type="secondary" style={{ fontSize: isMobile ? 10 : 11 }}>
                  Attr. Page Views:
                </Typography.Text>
              </Col>
              <Col span={10} style={{ textAlign: 'right' }}>
                <Typography.Text style={{ fontSize: isMobile ? 11 : 12 }}>
                  {formatWholeNumber(record.sessionAttrPageViews)}
                </Typography.Text>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    );
  };

  const columns = [
    {
      title: <TableColumnTitle text={'Name'} />,
      dataIndex: 'name',
      key: 'name',
      width: '15%',
      align: 'left',

      render: (text) => {
        return (
          <Row type="flex" justify="start">
            <Col>
              <PrimaryTableRowText text={text} />
            </Col>
          </Row>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Status'} />,
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      align: 'left',

      render: (text) => {
        const config = {
          ACTIVE: {
            backgroundColor: '#00DE5133',
            color: '#00DE51',
            text: 'Live',
            width: '50px',
          },
          DEMO: {
            backgroundColor: '#b9b6b633',
            color: '#b9b6b6',
            text: 'Demo',
            width: '60px',
          },
          PAUSED: {
            backgroundColor: 'orange',
            width: '70px',
            color: 'white',
            text: 'Paused',
          },
        };
        const status = text ? text : 'LIVE';
        return (
          <Row type="flex" justify="start">
            <Col>
              <Tag
                style={{
                  ...config[status],
                  border: 0,
                  borderRadius: '25px',
                }}
              >
                <Row justify="space-around" type="flex" align="middle">
                  <div
                    style={{
                      width: '5px',
                      height: '5px',
                      borderRadius: '50px',
                      backgroundColor: config[status].color,
                    }}
                  />
                  {config[status].text}
                </Row>
              </Tag>
            </Col>
          </Row>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Date Range'} />,
      dataIndex: 'startDate',
      key: 'startDate',
      align: 'left',
      width: '14%',
      footerContent: ' ',
      render: (text, record) => {
        if (!text) {
          return <div />;
        }
        const currentDate = moment().format('MM/DD/YY');
        const startDate = record.startDate ? moment(record.startDate) : null;
        const endDate = record.endDate ? moment(record.endDate) : null;
        let flightProgress = 0;
        if (currentDate > endDate) flightProgress = 100;
        else if (currentDate < endDate && currentDate > startDate)
          flightProgress =
            (currentDate.diff(startDate, 'minutes') / endDate.diff(startDate, 'minutes')) * 100;
        return (
          <Frag>
            <Row type="flex" justify="start">
              {record.startDate && (
                <Col>
                  <FlightDateText text={startDate ? startDate.format('MM/DD/YY') : null} />
                </Col>
              )}
              <Col>
                <FlightDateText text={<span>&nbsp; &#8594; &nbsp;</span>} />
              </Col>
              {record.endDate && (
                <Col>
                  <FlightDateText text={endDate ? endDate.format('MM/DD/YY') : null} />
                </Col>
              )}
            </Row>
            <Row type="flex" justify="start">
              <Progress
                id="main-campaign-table-progress"
                type={'line'}
                strokeWidth={3}
                percent={flightProgress ? flightProgress : 0}
                showInfo={false}
                strokeColor={'#636363'}
              />
            </Row>
          </Frag>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Sales'} />,
      dataIndex: 'transactions',
      key: 'transactions',
      align: 'left',
      width: '14%',
      render: (text, record) => {
        return (
          <Col>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Attr. Page Views:'} />
                <StandardTableRowText text={formatWholeNumber(record.clicks)} />
              </Space>
            </Row>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Customers:'} />
                <StandardTableRowText text={formatWholeNumber(record.customers)} />
              </Space>
            </Row>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Transactions:'} />
                <StandardTableRowText text={formatWholeNumber(record.transactions)} />
              </Space>
            </Row>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Revenue:'} />
                <StandardTableRowText text={formatCurrency2SigFig(record.revenue)} />
              </Space>
            </Row>
          </Col>
        );
      },
    },
    {
      title: <TableColumnTitle text={'Sessions'} />,
      dataIndex: 'sessions',
      key: 'sessions',
      align: 'left',
      width: '14%',
      render: (text, record) => {
        return (
          <Col>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Sessions:'} />
                <StandardTableRowText text={formatWholeNumber(record.sessions)} />
              </Space>
            </Row>
            <Row type="flex" justify="start">
              <Space>
                <PrimaryTableRowText text={'Attr. Page Views:'} />
                <StandardTableRowText text={formatWholeNumber(record.sessionAttrPageViews)} />
              </Space>
            </Row>
          </Col>
        );
      },
    },
  ];

  return (
    <div style={{ backgroundColor: '#fff', border: useCardLayout ? 'none' : '1px solid #e8e8e8', padding: useCardLayout ? (isMobile ? '0 12px' : '0 16px') : 0 }}>
      {useCardLayout ? (
        <div>
          {data && data.length > 0 ? (
            data
              .slice((localCurrent - 1) * localPageSize, localCurrent * localPageSize)
              .map((record) => <div key={record.id}>{renderMobileCard(record)}</div>)
          ) : (
            <div style={{ textAlign: 'center', padding: isMobile ? 24 : 48 }}>
              <Typography.Text type="secondary">No data available</Typography.Text>
            </div>
          )}
          {data && data.length > 0 && (
            <Row justify="center" style={{
              marginTop: isMobile ? 16 : 24,
              marginBottom: isMobile ? 16 : 24,
              padding: isMobile ? '12px 16px' : '16px 24px'
            }}>
              <Col>
                <Pagination
                  current={localCurrent}
                  total={data.length}
                  pageSize={localPageSize}
                  showSizeChanger
                  showQuickJumper
                  size="default"
                  onChange={(page, size) => {
                    setLocalCurrent(page);
                    sessionStorage.setItem('editorialMainCurrentPage', page.toString());
                    if (size !== localPageSize) {
                      setLocalPageSize(size);
                      sessionStorage.setItem('editorialMainPageSize', size.toString());
                    }
                  }}
                  onShowSizeChange={(current, size) => {
                    setLocalPageSize(size);
                    setLocalCurrent(1);
                    sessionStorage.setItem('editorialMainCurrentPage', '1');
                    sessionStorage.setItem('editorialMainPageSize', size.toString());
                  }}
                  itemRender={(page, type, originalElement) => {
                    if (type === 'page') {
                      return (
                        <a style={{
                          display: 'inline-block',
                          borderRadius: '50%',
                          minWidth: '32px',
                          height: '32px',
                          lineHeight: '30px',
                          textAlign: 'center'
                        }}>
                          {page}
                        </a>
                      );
                    }
                    return originalElement;
                  }}
                />
              </Col>
            </Row>
          )}
        </div>
      ) : (
        <Table
        onRow={(data) => ({
          onClick: () => {
            const { id } = data;
            history.push(`/publisher/details/${id}/1`);
          },
        })}
        dataSource={data}
        pagination={{
          hideOnSinglePage: false,
          position: ['none', 'bottomCenter'],
          showSizeChanger: true,
          showQuickJumper: true,
          size: 'default',
          pageSize: localPageSize,
          current: localCurrent,
          total: data ? data.length : 0,
          onChange: (page, size) => {
            setLocalCurrent(page);
            sessionStorage.setItem('editorialMainCurrentPage', page.toString());
            if (size !== localPageSize) {
              setLocalPageSize(size);
              sessionStorage.setItem('editorialMainPageSize', size.toString());
            }
          },
          onShowSizeChange: (current, size) => {
            setLocalPageSize(size);
            setLocalCurrent(1);
            sessionStorage.setItem('editorialMainCurrentPage', '1');
            sessionStorage.setItem('editorialMainPageSize', size.toString());
          },
          itemRender: (page, type, originalElement) => {
            if (type === 'page') {
              return (
                <a style={{
                  display: 'inline-block',
                  borderRadius: '50%',
                  minWidth: '32px',
                  height: '32px',
                  lineHeight: '30px',
                  textAlign: 'center'
                }}>
                  {page}
                </a>
              );
            }
            return originalElement;
          },
        }}
        size="default"
        columns={columns}
        scroll={{ x: 1200 }}
        bordered={false}
        rowKey={(record) => record.id}
        rowSelection={{
          onChange: onRowChange,
          selectedRowKeys,
          columnWidth: '5%',
        }}
      />
      )}
    </div>
  );
};

export default EditorialTable;
